import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { fetchReferrals } from "../../../services/referralService";
import { ReferralListItem } from "../../../types/fyo";
import PageHeader from "../../shared/PageHeader/PageHeader";
import Badge from "../../shared/Badge/Badge";
import { formatDateLong } from "../../../utils/formatters";
import "./ReferralsPage.css";

const LIMIT = 50;

interface Props {
  onViewCandidate: (id: number) => void;
  onSelect:        (opportunityResourceId: number) => void;
}

export default function ReferralsPage({ onViewCandidate, onSelect }: Props) {
  const location = useLocation();
  const preset = (location.state as { filters?: { status?: string } } | null)?.filters;

  const [rows,    setRows]    = useState<ReferralListItem[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // UI filter state
  const [filterName,          setFilterName]          = useState("");
  const [filterStatus,        setFilterStatus]        = useState(preset?.status ?? "");
  const [filterCompany,       setFilterCompany]       = useState("");
  const [filterWindowExpired, setFilterWindowExpired] = useState("");

  // Applied filter state — seeded from preset so dashboard navigation fires immediately
  const [applied, setApplied] = useState({
    name: "", status: preset?.status ?? "", company: "", windowExpired: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchReferrals({
      name:             applied.name    || undefined,
      status:           applied.status  || undefined,
      referred_company: applied.company || undefined,
      window_expired:   applied.windowExpired === "true"  ? true
                      : applied.windowExpired === "false" ? false
                      : undefined,
      limit:  LIMIT,
      offset,
    })
      .then((r: any) => { setRows(r.referrals ?? r.items ?? []); setTotal(r.total ?? 0); })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [applied, offset]);

  useEffect(() => { load(); }, [load]);

  const handleApply = () => {
    setApplied({ name: filterName, status: filterStatus, company: filterCompany, windowExpired: filterWindowExpired });
    setOffset(0);
  };

  return (
    <div className="fyo-page">
      <PageHeader title="Direct Placement" subtitle="All candidate referrals" />

      <div className="fyo-filter-bar">
        <input
          className="fyo-input"
          placeholder="Search by name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
          style={{ width: 200 }}
        />

        <input
          className="fyo-input"
          placeholder="Referred company"
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
          style={{ width: 200 }}
        />

        <select className="fyo-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="paused">Untrackable</option>
          <option value="placed">Placed</option>
          <option value="not_placed">Tracking Expired</option>
        </select>

        <select className="fyo-select" value={filterWindowExpired} onChange={(e) => setFilterWindowExpired(e.target.value)}>
          <option value="">All Tracking Statuses</option>
          <option value="false">Tracking Active</option>
          <option value="true">Tracking Expired</option>
        </select>

        <button className="fyo-btn fyo-btn--primary" onClick={handleApply}>Apply</button>
        <button className="fyo-btn fyo-btn--ghost" onClick={() => {
          setFilterName(""); setFilterStatus(""); setFilterCompany(""); setFilterWindowExpired("");
          setApplied({ name: "", status: "", company: "", windowExpired: "" });
          setOffset(0);
        }}>Clear</button>
      </div>

      {error && <div className="fyo-error-banner">{error}</div>}

      <div className="fyo-card fyo-card--no-pad">
        {loading ? (
          <p className="state-text state-text--muted" style={{ padding: "var(--space-6)" }}>Loading...</p>
        ) : (
          <>
            <div className="fyo-table-scroll">
              <table className="fyo-table">
              <thead>
                <tr>
                  <th>RMS Candidate</th>
                  <th>RMS Referred Company</th>
                  <th>RMS Referral ID</th>
                  <th>RMS Referral Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={5} className="fyo-muted" style={{ textAlign: "center", padding: "var(--space-6)" }}>No referrals found.</td></tr>
                ) : rows.map((r, i) => {
                  const missingLinkedin = r.status_reason === "no_linkedin_url";

                  const canNavigate = r.opportunity_resource_id != null;

                  return (
                    <tr
                      key={r.id}
                      className={`${i % 2 === 1 ? "fyo-table__row--odd" : ""} ${canNavigate ? "fyo-table__row--clickable" : ""}`}
                      onClick={() => canNavigate && onSelect(r.opportunity_resource_id!)}
                    >
                      <td className="fyo-table__cell--primary" onClick={(e) => e.stopPropagation()}>
                        <button className="fyo-link-btn" onClick={() => onViewCandidate(r.rms_candidate_id)}>
                          {r.candidate_name}
                        </button>
                        {missingLinkedin && (
                          <span className="fyo-badge--missing-linkedin" style={{ marginLeft: 6 }}>
                            Missing LinkedIn
                          </span>
                        )}
                        {r.candidate_email && (
                          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-tertiary)", marginTop: 2 }}>
                            {r.candidate_email}
                          </div>
                        )}
                      </td>
                      <td>
                        <span>{r.referred_company || "—"}</span>
                      </td>
                      <td>{r.opportunity_resource_id ?? <span className="fyo-muted">—</span>}</td>
                      <td>{formatDateLong(r.referral_date)}</td>
                      <td><Badge text={r.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>

            <div className="fyo-pagination">
              <span className="fyo-pagination__info">
                {total === 0 ? "No results" : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total}`}
              </span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}>Prev</button>
              <span className="fyo-muted">Page {Math.floor(offset / LIMIT) + 1} of {Math.ceil(total / LIMIT) || 1}</span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset + LIMIT >= total}
                onClick={() => setOffset((o) => o + LIMIT)}>Next</button>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
